// Static RCUK Adult ALS algorithm reference, summoned with `A`. Closes on `Esc` or backdrop click.
// Content is paraphrased from the RCUK 2021/2025 Adult ALS algorithm poster.
// Replace with PDF when scans land in rag-sources/.

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function AlgorithmReference({ open, onClose }: Props) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-ink-950/85 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="max-w-3xl mx-auto my-10 px-4" onClick={(e) => e.stopPropagation()}>
        <div className="panel p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-xs text-mono-white/40 uppercase tracking-widest">Reference · RCUK Adult ALS 2021</p>
              <h2 className="text-xl font-semibold mt-1">Advanced Life Support algorithm</h2>
            </div>
            <button onClick={onClose} className="btn-ghost text-sm">Close (Esc)</button>
          </div>

          <div className="text-sm space-y-3 leading-relaxed">
            <p className="text-mono-white/70 italic">Algorithm summary for in-hospital adult cardiac arrest. Always: high-quality CPR, minimise interruptions, treat reversible causes.</p>

            <Section title="1 · Unresponsive and not breathing normally">
              <ul className="list-disc list-inside space-y-1 text-mono-white/80">
                <li>Shake and shout · check responsiveness · shout for help (in hospital: <span className="font-mono">2222</span>)</li>
                <li>Open airway · look, listen, feel for breathing (≤10s)</li>
                <li>Start CPR · 30:2 · compress to 1/3 depth at 100–120/min</li>
              </ul>
            </Section>

            <Section title="2 · Attach defib / monitor">
              <ul className="list-disc list-inside space-y-1 text-mono-white/80">
                <li>Apply pads (anterior-lateral) without interrupting compressions if possible</li>
                <li>Brief rhythm check at 2-minute cycles · hands off ≤5s</li>
              </ul>
            </Section>

            <Section title="3a · Shockable (VF / pulseless VT)">
              <ul className="list-disc list-inside space-y-1 text-mono-white/80">
                <li><span className="text-mono-cyan">Shock</span> (biphasic 150–200J first shock; subsequent max output)</li>
                <li>Immediately resume CPR for 2 min</li>
                <li>Repeat rhythm check, shock if still shockable</li>
                <li><span className="text-alarm-amber">Adrenaline 1 mg IV/IO after the 3rd shock</span>, then every 3–5 min</li>
                <li><span className="text-alarm-amber">Amiodarone 300 mg IV/IO after the 3rd shock</span>; further 150 mg after the 5th shock if persistent</li>
                <li>Consider lidocaine 1–1.5 mg/kg as alternative</li>
              </ul>
            </Section>

            <Section title="3b · Non-shockable (PEA / asystole)">
              <ul className="list-disc list-inside space-y-1 text-mono-white/80">
                <li>Continue CPR · check rhythm every 2 min</li>
                <li><span className="text-alarm-amber">Adrenaline 1 mg IV/IO as soon as access available</span>, then every 3–5 min</li>
                <li>Do <b>not</b> shock asystole or PEA</li>
              </ul>
            </Section>

            <Section title="4 · During CPR — find and treat reversible causes (4Hs · 4Ts)">
              <div className="grid grid-cols-2 gap-3">
                <ul className="list-disc list-inside space-y-1 text-mono-white/80">
                  <li>Hypoxia</li>
                  <li>Hypovolaemia</li>
                  <li>Hypo-/hyperkalaemia, metabolic</li>
                  <li>Hypothermia</li>
                </ul>
                <ul className="list-disc list-inside space-y-1 text-mono-white/80">
                  <li>Tension pneumothorax</li>
                  <li>Tamponade — cardiac</li>
                  <li>Toxins</li>
                  <li>Thrombosis (PE / MI)</li>
                </ul>
              </div>
            </Section>

            <Section title="5 · Post-ROSC bundle">
              <ul className="list-disc list-inside space-y-1 text-mono-white/80">
                <li>ABCDE · 12-lead ECG</li>
                <li>SpO₂ target 94–98% · PaCO₂ 35–45 mmHg · MAP ≥65 mmHg</li>
                <li>Targeted temperature management 32–36 °C · avoid pyrexia &gt;37.5 °C</li>
                <li>Treat underlying cause · consider PPCI · ICU admission</li>
              </ul>
            </Section>

            <div className="pt-3 border-t border-ink-800 mt-4">
              <p className="text-[10px] text-mono-white/40 italic mb-2">
                Reference summary only. Full guidelines: resus.org.uk/library/2021-resuscitation-guidelines · always defer to current edition and local policy.
              </p>
              <details className="text-[10px] text-mono-white/40">
                <summary className="cursor-pointer hover:text-mono-white/60">📖 Source citations (RCUK OCR corpus, available to the authoring team)</summary>
                <ul className="mt-2 space-y-0.5 list-none pl-3 leading-relaxed">
                  <li>Algorithm overview — <span className="font-mono">A-06.md</span> (Ch 01 · Adult ALS in perspective)</li>
                  <li>Shockable & non-shockable arms — <span className="font-mono">B-20.md to B-24.md</span> (Ch 06)</li>
                  <li>Reversible causes (4Hs/4Ts) — <span className="font-mono">C-04.md, C-08.md</span></li>
                  <li>Drug doses — <span className="font-mono">F-08.md</span> (Appendix A)</li>
                  <li>Defibrillation energies & pad placement — <span className="font-mono">C-20.md..C-24.md</span> (Ch 09)</li>
                  <li>Post-ROSC bundle — <span className="font-mono">E-10.md..E-16.md</span> (Ch 13)</li>
                  <li>Peri-arrest tachy/brady — <span className="font-mono">D-11.md..D-15.md</span> (Ch 11)</li>
                </ul>
              </details>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-mono-cyan font-semibold mt-3 mb-1">{title}</h3>
      {children}
    </div>
  );
}
